import React, { forwardRef } from 'react';
import { InlineError } from './InlineError';
import { HelpText } from './HelpText';
import styles from './input.module.scss';

export type TextAreaProps =
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    label?: React.ReactNode;
    helpText?: React.ReactNode;
    error?: React.ReactNode;
    maxLengthLabel?: boolean;
  };

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  function TextArea(
    {
      label,
      id,
      helpText,
      error,
      maxLength,
      value,
      maxLengthLabel = true,
      style,
      className,
      ...rest
    },
    ref
  ) {
    const inputId = id ?? rest.name ?? undefined;
    const helpId = helpText ? `${inputId}-help` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;
    const length = typeof value === 'string' ? value.length : undefined;

    return (
      <label
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          minWidth: 0
        }}
      >
        {label && (
          <span
            style={{
              fontWeight: 'var(--font-weight-bold)',
              fontSize: 'var(--font-size-base)',
              lineHeight: '1.5rem',
              letterSpacing: '0.01em',
              color: 'var(--color-text-primary, #1a1a1a)'
            }}
          >
            {label}
          </span>
        )}
        <textarea
          {...rest}
          id={inputId}
          ref={ref}
          className={`${className} ${styles.textarea}`}
          value={value}
          maxLength={maxLength}
          aria-describedby={
            [helpId, errorId].filter(Boolean).join(' ') || undefined
          }
          aria-invalid={Boolean(error)}
          style={style}
        />
        {maxLength && maxLengthLabel && (
          <div
            style={{
              fontSize: '0.75rem',
              color: 'var(--color-text-secondary, #6b7280)'
            }}
          >
            {length ?? 0}/{maxLength}
          </div>
        )}
        {helpText && <HelpText id={helpId}>{helpText}</HelpText>}
        {error && <InlineError id={errorId}>{error}</InlineError>}
      </label>
    );
  }
);
